import { logger } from "@/lib/logger"

import type {
  CompletionResponse,
  GetCompletionMutation,
} from "./mimicLLMBinaryManager"
import type { TranscriptMessage } from "./SimulationWithContext"

const log = logger("MimicNumericalLLMJudge")

interface NumericEvalResult {
  answer: number | null
  explanation: string
  decision_message_indices: number[]
}

const PROMPT_TEMPLATE = [
  "I'm going to give you a question, a transcript and a number range.",
  "Evaluate the transcript based on the prompt and the number range.",
  "You will only reply with a number in the given range.",
  "Number Range: {min_value} to {max_value}.",
  "Question: {question}",
  "Transcript: {transcript}",
].join("\n")

const SYSTEM_PROMPT = [
  "You are an assistant that evaluates a transcript based on a question and returns a number.",
  "1. Read the entire transcript.",
  "2. Identify where in the transcript you need to look to make your decision.",
  "3. Make your decision.",
  "4. Return your answer and explanation in the specific format.",
].join("\n")

const EVALUATION_FUNCTION = {
  type: "function",
  function: {
    name: "evaluation_response",
    description: "Response schema for evaluating a transcript or audio.",
    parameters: {
      type: "object",
      properties: {
        answer: {
          type: "number",
          description:
            "The numerical evaluation of the transcript based on the question and the given number range.",
        },
        explanation: {
          type: "string",
          description:
            "A single, concise sentence (<20 words) explaining the reasoning.",
        },
        decision_message_indices: {
          type: "array",
          items: { type: "integer" },
          maxItems: 10,
          description:
            "List of the first 10 or fewer message indices that directly support the decision.",
        },
      },
      required: ["answer", "explanation", "decision_message_indices"],
      additionalProperties: false,
    },
  },
}

const formatTranscript = (messages: TranscriptMessage[]): string =>
  messages
    .map((msg, idx) => {
      const index = Number.isInteger(msg.index) ? msg.index : idx
      return `[${index}] ${msg.role}: ${msg.content}`
    })
    .join("\n")

const parseToolArgs = (
  response: CompletionResponse,
): Partial<NumericEvalResult> => {
  try {
    const call =
      response.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments ??
      response.tool_calls?.[0]?.function?.arguments
    if (!call) {
      return {}
    }
    const parsed: unknown = typeof call === "string" ? JSON.parse(call) : call
    if (typeof parsed !== "object" || parsed === null) {
      return {}
    }
    const obj = parsed as Record<string, unknown>
    return {
      // answer will be validated/parsed below
      answer:
        typeof obj.answer === "number"
          ? obj.answer
          : typeof obj.answer === "string"
            ? parseFloat(obj.answer)
            : undefined,
      explanation:
        typeof obj.explanation === "string" ? obj.explanation : undefined,
      decision_message_indices: Array.isArray(obj.decision_message_indices)
        ? (obj.decision_message_indices as number[])
        : undefined,
    }
  } catch {
    return {}
  }
}

const validateNumber = (
  raw: unknown,
  minValue?: number,
  maxValue?: number,
): number | null => {
  const num = typeof raw === "number" ? raw : Number(raw)
  if (!Number.isFinite(num)) {
    return null
  }
  if (minValue !== undefined && num < minValue) {
    return null
  }
  if (maxValue !== undefined && num > maxValue) {
    return null
  }
  return num
}

export const mimicNumericalLLMJudge = async (
  question: string,
  transcript: TranscriptMessage[],
  getCompletionMutation: GetCompletionMutation,
  minValue: number,
  maxValue: number,
  questionDefinition?: string,
  testCaseExpectedOutput?: string,
  model = "openai/gpt-4o-mini",
): Promise<NumericEvalResult> => {
  const fallback: NumericEvalResult = {
    answer: null,
    explanation: "Error processing request",
    decision_message_indices: [],
  }

  try {
    if (!question) {
      throw new Error("question is required")
    }
    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
      throw new Error("minValue and maxValue are required numbers")
    }

    const transcriptString = formatTranscript(transcript)
    const userPrompt = PROMPT_TEMPLATE.replace("{question}", question)
      .replace("{transcript}", transcriptString)
      .replace("{min_value}", String(minValue))
      .replace("{max_value}", String(maxValue))

    const messages = [
      { role: "system" as const, content: SYSTEM_PROMPT },
      { role: "user" as const, content: userPrompt },
    ]

    const response = await getCompletionMutation.mutateAsync({
      messages,
      tools: [EVALUATION_FUNCTION],
      tool_choice: {
        type: "function",
        function: { name: "evaluation_response" },
      },
      model,
      max_tokens: 1500,
      // Optional metadata passthrough; remove if your API doesn't support these
      ...(questionDefinition ? { questionDefinition } : {}),
      ...(testCaseExpectedOutput ? { testCaseExpectedOutput } : {}),
    })

    const parsed = parseToolArgs(response)
    const validated = validateNumber(parsed.answer, minValue, maxValue)
    const explanation =
      typeof parsed.explanation === "string"
        ? parsed.explanation
        : "No explanation provided"
    const decisionMessageIndices = Array.isArray(
      parsed.decision_message_indices,
    )
      ? parsed.decision_message_indices
          .filter((n) => Number.isInteger(n))
          .map(Number)
      : []

    return {
      answer: validated,
      explanation,
      decision_message_indices: decisionMessageIndices,
    }
  } catch (error) {
    log.error("mimicNumericalLLMJudge error", error)
    return fallback
  }
}
